import React, { useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react';
import axios from 'axios';
import { Alert, Badge, Button, Form, Spinner, Table } from 'react-bootstrap';
import { AuthContext } from '../../context/AuthContext';
import { useDialog } from '../../context/DialogContext';
import PdfPreviewModal from '../../components/common/PdfPreviewModal';
import { queueScoreSave } from '../../utils/offlineQueue';

// Interactive scoring guide for RLP/OSM modules, reproducing UVTAB's printed
// template structure (criteria grouped under questions, with Sub-Total/Total
// rows and a percentage Grand Total).
//
// Candidates are a SHARED POOL for the whole team, not pre-split: nothing is
// bulk-loaded up front - the examiner searches the roster (by registration
// number or name) and picks a candidate to work on. A candidate that isn't
// yet COMPLETE or ABSENT can be scored by any team member; once it's
// COMPLETE it locks immediately (see CandidateAllocationService/
// CandidateScoreService on the backend) - there's no separate "merge" step.
// Each examiner can review their own scored candidates (read-only), and the
// team leader (or solo examiner) can see the full roster with who scored
// what and drill into any candidate read-only.
export default function ScoringGuideTable({
  moduleId,
  assignmentId,
  examCenterId,
  assignmentInfo,
  moduleDetails,
  uploadWindowOpen,
  onCompletionChange
}) {
  const { user } = useContext(AuthContext);
  const { prompt, confirm } = useDialog();

  const [criteria, setCriteria] = useState([]);
  const [allScores, setAllScores] = useState([]); // flat CandidateScoreDTO list, every candidate this examiner can see
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  // Search - nothing is bulk-loaded; results come from the backend search
  // endpoint, debounced.
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [selectedCandidate, setSelectedCandidate] = useState(null); // {registrationNumber, name, status, scoredByExaminerId, scoredByExaminerName, underProtest}
  const [draftScores, setDraftScores] = useState({}); // criterionId -> { process, product }
  const [autosaveStatus, setAutosaveStatus] = useState('idle'); // idle | dirty | saving | saved | error
  const [markingAbsent, setMarkingAbsent] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  const [myScored, setMyScored] = useState([]);
  const [showMyScored, setShowMyScored] = useState(false);
  const [loadingMyScored, setLoadingMyScored] = useState(false);

  const [teamReport, setTeamReport] = useState(null); // null until first loaded
  const [showTeamReport, setShowTeamReport] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);

  const [previewKind, setPreviewKind] = useState(null); // 'marksheet' | 'guide' | null
  const [protestFormOpen, setProtestFormOpen] = useState(false);
  const [protestForm, setProtestForm] = useState({ registrationNumber: '', name: '', email: '' });
  const [protestSubmitting, setProtestSubmitting] = useState(false);

  const isTeamAssignment = (assignmentInfo?.examiners?.length || 0) > 1;
  const isTeamLeader = isTeamAssignment && assignmentInfo?.teamLeader?.id === user?.id;
  const canSeeTeamReport = !isTeamAssignment || isTeamLeader;
  const assignmentCompleted = assignmentInfo?.status === 'COMPLETED';

  // Autosave: refs mirror the latest state so the debounce timer/beforeunload
  // handler (both created once, outside React's render cycle) always see
  // fresh values instead of a stale closure from whichever render scheduled
  // them.
  const draftScoresRef = useRef({});
  const selectedCandidateRef = useRef(null);
  const criteriaRef = useRef([]);
  const scoringLockedRef = useRef(false);
  const debounceTimerRef = useRef(null);
  const savingRef = useRef(false);
  const pendingRerunRef = useRef(false);

  // Criteria + every score this examiner is entitled to see - needed to
  // render whichever candidate is currently selected and to compute local
  // completion. NOT the roster itself - that's search-driven below.
  const loadCriteriaAndScores = useCallback(async () => {
    setLoading(true);
    try {
      const [criteriaRes, scoresRes] = await Promise.all([
        axios.get(`/api/scoring-criteria/module/${moduleId}`),
        axios.get(`/api/candidate-scores/assignment/${assignmentId}`)
      ]);
      setCriteria(criteriaRes.data || []);
      setAllScores(scoresRes.data || []);
    } catch (err) {
      // A 401 here means the token just expired - AuthContext's interceptor
      // is already locking the screen in place for it, so a plain-text
      // error banner underneath would just be confusing, stale noise once
      // the user unlocks.
      if (err.response?.status !== 401) {
        setFeedback({ type: 'danger', message: 'Error loading scoring guide.' });
      }
    } finally {
      setLoading(false);
    }
  }, [moduleId, assignmentId]);

  useEffect(() => {
    loadCriteriaAndScores();
  }, [loadCriteriaAndScores]);

  const fetchTeamReport = useCallback(() => {
    if (!canSeeTeamReport) {
      setTeamReport([]);
      return;
    }
    setLoadingReport(true);
    axios.get(`/api/candidate-allocations/assignment/${assignmentId}/report`)
      .then((res) => setTeamReport(res.data || []))
      .catch(() => setTeamReport([]))
      .finally(() => setLoadingReport(false));
  }, [assignmentId, canSeeTeamReport]);

  useEffect(() => {
    fetchTeamReport();
  }, [fetchTeamReport]);

  const fetchMyScored = useCallback(() => {
    setLoadingMyScored(true);
    axios.get(`/api/candidate-allocations/assignment/${assignmentId}/mine`)
      .then((res) => setMyScored(res.data || []))
      .catch(() => setMyScored([]))
      .finally(() => setLoadingMyScored(false));
  }, [assignmentId]);

  // Audit trail: record one "viewed" session per visit to this scoring
  // guide, from the moment it's actually loaded (not on every re-fetch
  // after a save) through however long the examiner keeps it open. Ends via
  // fetch+keepalive (works during page unload, unlike a plain axios call)
  // on both an in-app navigation away and a hard tab close/refresh.
  const viewLogIdRef = useRef(null);
  const viewStartedRef = useRef(false);

  useEffect(() => {
    if (viewStartedRef.current || criteria.length === 0) return;
    viewStartedRef.current = true;
    axios.post(`/api/scoring-guide-audit/module/${moduleId}/view-start`, null, { params: { assignmentId } })
      .then((res) => { viewLogIdRef.current = res.data?.id || null; })
      .catch(() => {});
  }, [criteria, moduleId, assignmentId]);

  useEffect(() => {
    const endSession = () => {
      if (!viewLogIdRef.current) return;
      const logId = viewLogIdRef.current;
      viewLogIdRef.current = null;
      const token = localStorage.getItem('token');
      fetch(`/api/scoring-guide-audit/${logId}/view-end`, {
        method: 'POST',
        keepalive: true,
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    };
    window.addEventListener('beforeunload', endSession);
    return () => {
      window.removeEventListener('beforeunload', endSession);
      endSession();
    };
  }, []);

  // Criteria grouped by question, in upload order. Criteria uploaded before
  // this feature existed (no question grouping) fall back into one group.
  const questionGroups = useMemo(() => {
    const groups = [];
    const byNumber = new Map();
    criteria.forEach((c) => {
      const qNum = c.questionNumber ?? 0;
      if (!byNumber.has(qNum)) {
        const group = { questionNumber: qNum, questionLabel: c.questionLabel || 'Questions', items: [] };
        byNumber.set(qNum, group);
        groups.push(group);
      }
      byNumber.get(qNum).items.push(c);
    });
    return groups;
  }, [criteria]);

  // The module can set a fixed max score (for when candidates only attempt
  // a subset of the questions) - otherwise fall back to summing every
  // criterion's max scores.
  const totalMax = useMemo(() => {
    if (moduleDetails?.maxScore && moduleDetails.maxScore > 0) return moduleDetails.maxScore;
    return criteria.reduce((sum, c) => sum + (c.maxScoreProcess || 0) + (c.maxScoreProduct || 0), 0);
  }, [criteria, moduleDetails]);

  // How many distinct questions a candidate must fully score. Not set (or
  // <= 0) means every question on the guide is required.
  const requiredQuestions = useMemo(() => {
    const configured = moduleDetails?.requiredQuestionsCount;
    if (!configured || configured <= 0) return questionGroups.length;
    return Math.min(configured, questionGroups.length);
  }, [moduleDetails, questionGroups]);

  // regNo -> { criterionId -> { process, product } }
  const scoresByStudent = useMemo(() => {
    const map = {};
    allScores.forEach((s) => {
      if (!map[s.registrationNumber]) map[s.registrationNumber] = {};
      map[s.registrationNumber][s.scoringCriterionId] = { process: s.processScore, product: s.productScore };
    });
    return map;
  }, [allScores]);

  // Client-side completion check for any candidate we already have scores
  // for - gates the "Finalize & Lock Scores" action (only shown once the
  // required number of questions is scored) and determines the badge shown
  // for candidates opened from "My Scored". Reaching this point never locks
  // anything by itself - autosave keeps saving freely past this point until
  // the examiner explicitly finalizes (see handleFinalize).
  const isRegNoLocallyComplete = useCallback((regNo) => {
    const scored = scoresByStudent[regNo] || {};
    const fullyScoredQuestions = questionGroups.filter((g) => g.items.every((c) => scored[c.id])).length;
    return questionGroups.length > 0 && fullyScoredQuestions >= requiredQuestions;
  }, [scoresByStudent, questionGroups, requiredQuestions]);

  // Debounced search against the shared candidate pool.
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const handle = setTimeout(() => {
      axios.get(`/api/candidate-allocations/assignment/${assignmentId}/search`, { params: { query: q } })
        .then((res) => setSearchResults(res.data || []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [searchQuery, assignmentId]);

  // Load the selected candidate's existing scores into the editable draft.
  // Any pending autosave for the PREVIOUS candidate is flushed by the
  // caller (see the candidate-picker buttons below) before the selection
  // changes, so this never clobbers an unsaved edit.
  useEffect(() => {
    setAutosaveStatus('idle');
    if (!selectedCandidate) {
      setDraftScores({});
      return;
    }
    const regNo = selectedCandidate.registrationNumber;
    setDraftScores(scoresByStudent[regNo] ? { ...scoresByStudent[regNo] } : {});
  }, [selectedCandidate, scoresByStudent]);

  const handleCellChange = (criterionId, field, rawValue, max) => {
    const value = rawValue === '' ? null : Math.max(0, Math.min(max, Number(rawValue)));
    setDraftScores((prev) => ({
      ...prev,
      [criterionId]: { ...(prev[criterionId] || {}), [field]: value }
    }));
    scheduleAutosave();
  };

  const candidateTotal = (field) => {
    return criteria.reduce((sum, c) => {
      const cell = draftScores[c.id];
      return sum + (cell && cell[field] != null ? cell[field] : 0);
    }, 0);
  };

  const groupTotal = (group, field) => {
    return group.items.reduce((sum, c) => {
      const cell = draftScores[c.id];
      return sum + (cell && cell[field] != null ? cell[field] : 0);
    }, 0);
  };

  const grandPercentage = totalMax === 0 ? 0 : ((candidateTotal('process') + candidateTotal('product')) / totalMax) * 100;

  const selectedIsLocked = !!selectedCandidate
    && (selectedCandidate.status === 'COMPLETE' || selectedCandidate.status === 'ABSENT');
  // Once a candidate is IN_PROGRESS, only the examiner who started them may
  // continue - the backend rejects anyone else's saves outright
  // (CandidateScoreService.saveScores), so the table is shown read-only here
  // too rather than letting someone type and only find out on autosave.
  const scoringRestrictedToOtherExaminer = !!selectedCandidate
    && selectedCandidate.status === 'IN_PROGRESS'
    && !!selectedCandidate.scoredByExaminerId
    && selectedCandidate.scoredByExaminerId !== user?.id;
  const scoringLocked = !uploadWindowOpen || assignmentCompleted || selectedIsLocked || scoringRestrictedToOtherExaminer;

  // Whether the WHOLE roster (every candidate COMPLETE or ABSENT) is ready
  // for the job to be marked complete - drives the parent's "Mark Uploading
  // as Complete" gate and the Preview buttons. Only meaningful for the team
  // leader/solo examiner (the only ones who can see the full roster at
  // all); false/irrelevant for a regular team member, who has no action
  // gated on it anyway.
  const teamAllReady = canSeeTeamReport && teamReport
    ? teamReport.every((r) => r.status === 'COMPLETE' || r.status === 'ABSENT')
    : false;

  useEffect(() => {
    if (loading) return;
    onCompletionChange(teamAllReady);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamAllReady, loading]);

  // Keep refs in sync so the debounce timer and the beforeunload flush
  // (below) always read the latest values without needing to be recreated
  // on every keystroke.
  useEffect(() => { draftScoresRef.current = draftScores; }, [draftScores]);
  useEffect(() => { selectedCandidateRef.current = selectedCandidate; }, [selectedCandidate]);
  useEffect(() => { criteriaRef.current = criteria; }, [criteria]);
  useEffect(() => { scoringLockedRef.current = scoringLocked; }, [scoringLocked]);

  const buildScorePayload = (criteriaList, regNo, scores) =>
    criteriaList
      .filter((c) => scores[c.id] && (scores[c.id].process != null || scores[c.id].product != null))
      .map((c) => ({
        scoringCriterionId: c.id,
        registrationNumber: regNo,
        processScore: scores[c.id].process ?? 0,
        productScore: scores[c.id].product ?? 0
      }));

  // The actual autosave network call - reads from refs (not component
  // state) so it behaves correctly whether it's invoked from the debounce
  // timer, a candidate-switch flush, or the beforeunload handler. Silent on
  // success (just updates the status pill + the saved scores, no Alert - that
  // would nag or cause table flicker while someone's still typing) but
  // surfaces failures via the same Alert used elsewhere. Never locks the
  // table on its own, no matter how complete the data becomes - only an
  // explicit, confirmed handleFinalize call does that.
  const performAutosave = useCallback(async () => {
    if (savingRef.current) {
      pendingRerunRef.current = true;
      return;
    }
    if (scoringLockedRef.current) return;
    const candidate = selectedCandidateRef.current;
    if (!candidate) return;
    const payload = buildScorePayload(criteriaRef.current, candidate.registrationNumber, draftScoresRef.current);
    if (payload.length === 0) return;

    savingRef.current = true;
    setAutosaveStatus('saving');
    try {
      const res = await axios.post(`/api/candidate-scores/assignment/${assignmentId}`, payload);
      setAllScores(res.data || []);
      setAutosaveStatus('saved');
    } catch (err) {
      if (!err.response) {
        // No response at all means the request never reached the server -
        // offline, not a rejection. Queue it locally instead of showing a
        // scary error; the ConnectivityContext sync manager will replay it
        // once the connection is confirmed back.
        await queueScoreSave(assignmentId, candidate.registrationNumber, payload);
        setAutosaveStatus('queued');
      } else {
        setAutosaveStatus('error');
        const backendMessage = typeof err.response.data === 'string'
          ? err.response.data
          : 'Autosave failed - your latest changes are not yet saved.';
        setFeedback({ type: 'danger', message: backendMessage });
      }
    } finally {
      savingRef.current = false;
      if (pendingRerunRef.current) {
        pendingRerunRef.current = false;
        performAutosave();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId]);

  // Debounced trigger, called on every keystroke - restarts a short idle
  // timer rather than saving on every character, so typing "15" doesn't
  // fire two requests.
  const scheduleAutosave = useCallback(() => {
    setAutosaveStatus('dirty');
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      performAutosave();
    }, 1500);
  }, [performAutosave]);

  // Forces a pending debounced save to run immediately - used right before
  // switching to a different candidate (whose useEffect below would
  // otherwise silently overwrite draftScores) and on unmount/navigation.
  const flushPendingAutosave = useCallback(() => {
    if (!debounceTimerRef.current) return;
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = null;
    performAutosave();
  }, [performAutosave]);

  // Best-effort save on hard navigation/tab close, mirroring the audit
  // view-end ping's fetch+keepalive pattern below (a plain axios call
  // isn't reliable during unload).
  useEffect(() => {
    const flushOnExit = () => {
      if (!debounceTimerRef.current) return;
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
      const candidate = selectedCandidateRef.current;
      if (!candidate || scoringLockedRef.current) return;
      const payload = buildScorePayload(criteriaRef.current, candidate.registrationNumber, draftScoresRef.current);
      if (payload.length === 0) return;
      const token = localStorage.getItem('token');
      fetch(`/api/candidate-scores/assignment/${assignmentId}`, {
        method: 'POST',
        keepalive: true,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(() => {});
    };
    window.addEventListener('beforeunload', flushOnExit);
    return () => {
      window.removeEventListener('beforeunload', flushOnExit);
      flushOnExit();
    };
  }, [assignmentId]);

  // Manual, explicit action - only offered once the candidate has been
  // scored for the required number of questions (isRegNoLocallyComplete).
  // Autosave alone never reaches this; it just keeps saving quietly no
  // matter how complete the data gets. Finalizing first flushes the latest
  // draft to the server (so the backend's own completeness check, which
  // reads persisted scores, agrees with what's on screen), then asks the
  // examiner to confirm the consequences before actually locking the
  // candidate - this call is what turns their status into COMPLETE.
  const handleFinalize = async () => {
    if (!uploadWindowOpen) {
      setFeedback({ type: 'danger', message: 'Scores can only be saved during the exam upload window.' });
      return;
    }
    if (assignmentCompleted) {
      setFeedback({ type: 'danger', message: 'This job has already been marked complete and can no longer be edited.' });
      return;
    }
    if (!selectedCandidate || selectedIsLocked) return;
    if (!isRegNoLocallyComplete(selectedCandidate.registrationNumber)) return;

    const proceed = await confirm(
      `You are about to finalize and lock scores for ${selectedCandidate.name} (${selectedCandidate.registrationNumber}). ` +
        'Once locked, no one - including you - will be able to change these scores again. Please make sure every score entered is correct before continuing.',
      { title: 'Finalize Candidate Scores', confirmText: 'Finalize & Lock', variant: 'danger' }
    );
    if (!proceed) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setFinalizing(true);
    setFeedback(null);
    try {
      const payload = buildScorePayload(criteria, selectedCandidate.registrationNumber, draftScores);
      if (payload.length > 0) {
        const res = await axios.post(`/api/candidate-scores/assignment/${assignmentId}`, payload);
        setAllScores(res.data || []);
      }
      await axios.post(`/api/candidate-allocations/assignment/${assignmentId}/finalize`, null, {
        params: { registrationNumber: selectedCandidate.registrationNumber }
      });
      setAutosaveStatus('saved');
      setSelectedCandidate((prev) => (prev && prev.registrationNumber === selectedCandidate.registrationNumber
        ? { ...prev, status: 'COMPLETE', scoredByExaminerId: user?.id, scoredByExaminerName: user?.fullName }
        : prev));
      setFeedback({ type: 'success', message: `${selectedCandidate.name} (${selectedCandidate.registrationNumber}) has been finalized and locked.` });
      fetchTeamReport();
    } catch (err) {
      const backendMessage = (err.response && typeof err.response.data === 'string')
        ? err.response.data
        : 'Error finalizing candidate.';
      setFeedback({ type: 'danger', message: backendMessage });
    } finally {
      setFinalizing(false);
    }
  };

  // Marks the selected (or a searched-up) candidate as absent - reuses the
  // exact same CandidateWorkStatus flow already used for CAD candidates
  // (ExamUploadPage.handleMarkAbsent), just exposed here for OSM/RLP too.
  const handleMarkAbsent = async (candidate) => {
    const reason = await prompt(
      `Confirm that ${candidate.name} (${candidate.registrationNumber}) did not sit this exam and should be marked ABSENT. Provide a reason for accountability:`,
      { title: 'Mark Candidate Absent', confirmText: 'Mark Absent', variant: 'danger', placeholder: 'e.g. Candidate did not report to the exam centre' }
    );
    if (reason === null) return;
    setMarkingAbsent(true);
    setFeedback(null);
    try {
      await axios.post(`/api/assignments/${assignmentId}/candidate-status`, [
        { registrationNumber: candidate.registrationNumber, status: 'ABSENT', reason }
      ]);
      setFeedback({ type: 'success', message: `${candidate.name} (${candidate.registrationNumber}) marked absent.` });
      setSelectedCandidate({ ...candidate, status: 'ABSENT' });
      const q = searchQuery.trim();
      if (q) {
        axios.get(`/api/candidate-allocations/assignment/${assignmentId}/search`, { params: { query: q } })
          .then((res) => setSearchResults(res.data || []))
          .catch(() => {});
      }
      fetchTeamReport();
    } catch (err) {
      const backendMessage = err.response?.data?.error || (typeof err.response?.data === 'string' ? err.response.data : null);
      setFeedback({ type: 'danger', message: backendMessage || 'Failed to mark candidate absent.' });
    } finally {
      setMarkingAbsent(false);
    }
  };

  // Examiners get read-only preview only - no .xlsx or .pdf download of
  // either document. Security/Data Team retain full download access via
  // their own pages (DataTeamSeriesWorkDownload.js), using the same backend
  // access rule enforced in ScoringGuideExportService.

  // Adds a genuine candidate who isn't on this centre's roster, recorded as
  // having sat under protest - mirrors the same flow used for CAD candidate
  // work uploads. They're immediately scoreable once added, and (on a split
  // team) allocated to whoever added them.
  const handleAddProtestCandidate = async () => {
    const { registrationNumber, name, email } = protestForm;
    if (!registrationNumber.trim() || !name.trim() || !email.trim()) {
      setFeedback({ type: 'danger', message: 'Registration number, name, and email are all required to add a candidate under protest.' });
      return;
    }
    if (!moduleDetails?.program?.id || !examCenterId) {
      setFeedback({ type: 'danger', message: 'Missing program/exam centre context - cannot add candidate.' });
      return;
    }

    // Registration numbers elsewhere in the system use "/" as the segment
    // separator - normalize a hyphenated value rather than saving it
    // verbatim and making it unsearchable/inconsistent with the roster.
    const rawRegNo = registrationNumber.trim();
    const normalizedRegNo = (!rawRegNo.includes('/') && rawRegNo.includes('-'))
      ? rawRegNo.replace(/-/g, '/')
      : rawRegNo;

    setProtestSubmitting(true);
    setFeedback(null);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim(),
        registrationNumber: normalizedRegNo,
        program: { id: moduleDetails.program.id },
        examCenter: { id: examCenterId },
        yearOfStudy: moduleDetails.yearOfStudy ? { id: moduleDetails.yearOfStudy.id } : null,
        semester: moduleDetails.semester ? { id: moduleDetails.semester.id } : null,
        status: 'active',
        underProtest: true
      };
      const res = await axios.post('/api/students', payload);
      const newStudent = res.data;
      try {
        await axios.post(`/api/candidate-allocations/assignment/${assignmentId}/self?studentId=${newStudent.id}`);
      } catch (allocErr) {
        console.error('Error self-allocating protest candidate:', allocErr);
      }
      setSearchQuery('');
      setSearchResults([]);
      setSelectedCandidate({
        registrationNumber: newStudent.registrationNumber,
        name: newStudent.name,
        status: 'NOT_STARTED',
        scoredByExaminerId: null,
        scoredByExaminerName: null,
        underProtest: true
      });
      setProtestFormOpen(false);
      setProtestForm({ registrationNumber: '', name: '', email: '' });
      const formatNote = normalizedRegNo !== rawRegNo ? ` (saved as ${normalizedRegNo} to match the system's registration number format)` : '';
      setFeedback({ type: 'success', message: `${name} added to the roster (sat under protest)${formatNote}. You can score them below.` });
    } catch (err) {
      const backendMessage = err.response?.data?.error || (typeof err.response?.data === 'string' ? err.response.data : null);
      setFeedback({ type: 'danger', message: backendMessage || 'Failed to add candidate under protest. The registration number or email may already be in use.' });
    } finally {
      setProtestSubmitting(false);
    }
  };

  const selectCandidate = (candidate) => {
    flushPendingAutosave();
    setSelectedCandidate(candidate);
  };

  const statusBadgeVariant = (status) => {
    switch (status) {
      case 'COMPLETE': return 'success';
      case 'ABSENT': return 'dark';
      case 'IN_PROGRESS': return 'warning';
      default: return 'secondary';
    }
  };

  const renderAddProtestSection = () => (
    <div className="mb-3 p-3" style={{ backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '6px' }}>
      {!protestFormOpen ? (
        <Button variant="outline-warning" size="sm" onClick={() => setProtestFormOpen(true)}>
          Add candidate under protest (not on roster)
        </Button>
      ) : (
        <div style={{ maxWidth: 'min(420px, 100%)' }}>
          <div className="small text-muted mb-2">
            For a genuine candidate not on this centre's roster. They'll be recorded as having sat under protest
            and can be scored immediately once added.
          </div>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-0">Registration Number</Form.Label>
            <Form.Control
              size="sm"
              placeholder="e.g. UVT660/U/26/M/NDICT/0016"
              value={protestForm.registrationNumber}
              onChange={(e) => setProtestForm((prev) => ({ ...prev, registrationNumber: e.target.value }))}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-0">Full Name</Form.Label>
            <Form.Control
              size="sm"
              value={protestForm.name}
              onChange={(e) => setProtestForm((prev) => ({ ...prev, name: e.target.value }))}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-0">Email</Form.Label>
            <Form.Control
              size="sm"
              type="email"
              value={protestForm.email}
              onChange={(e) => setProtestForm((prev) => ({ ...prev, email: e.target.value }))}
            />
          </Form.Group>
          <div className="d-flex flex-wrap gap-2">
            <Button size="sm" variant="warning" disabled={protestSubmitting} onClick={handleAddProtestCandidate}>
              {protestSubmitting ? 'Adding...' : 'Add & Score (Sat Under Protest)'}
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setProtestFormOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  const renderSearchSection = () => (
    <div className="mb-3 p-3" style={{ backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '6px' }}>
      <Form.Label className="fw-bold mb-2">Search the candidate roster by registration number or name</Form.Label>
      <Form.Control
        type="text"
        placeholder="e.g. UVT660/U/26/M/NDICT/00600"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="mb-2"
      />
      <div className="d-flex flex-wrap gap-2" style={{ maxHeight: 260, overflowY: 'auto' }}>
        {!searchQuery.trim() ? (
          <span className="text-muted small">Type a registration number or name to find a candidate.</span>
        ) : searching ? (
          <Spinner animation="border" size="sm" />
        ) : searchResults.length === 0 ? (
          <span className="text-muted small">No candidates match "{searchQuery}".</span>
        ) : (
          searchResults.map((s) => (
            <Button
              key={s.registrationNumber}
              size="sm"
              variant={selectedCandidate?.registrationNumber === s.registrationNumber ? 'primary' : 'outline-secondary'}
              onClick={() => selectCandidate(s)}
            >
              {s.name} ({s.registrationNumber}){' '}
              {s.underProtest && <Badge bg="danger" className="me-1">Under Protest</Badge>}
              <Badge bg={statusBadgeVariant(s.status)} text={s.status === 'IN_PROGRESS' ? 'dark' : undefined}>
                {s.status.replace('_', ' ')}
              </Badge>
              {s.scoredByExaminerName && s.status !== 'NOT_STARTED' && (
                <span className="small ms-1">- {s.scoredByExaminerName}</span>
              )}
            </Button>
          ))
        )}
      </div>
    </div>
  );

  const renderMyScoredSection = () => (
    <div className="mb-3 p-3" style={{ backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '6px' }}>
      <Button
        variant="link"
        className="p-0 fw-bold"
        onClick={() => {
          const next = !showMyScored;
          setShowMyScored(next);
          if (next && myScored.length === 0) fetchMyScored();
        }}
      >
        {showMyScored ? 'Hide' : 'Show'} My Scored Candidates (review)
      </Button>
      {showMyScored && (
        loadingMyScored ? (
          <div className="mt-2"><Spinner animation="border" size="sm" /></div>
        ) : myScored.length === 0 ? (
          <div className="text-muted small mt-2">You haven't scored any candidates yet.</div>
        ) : (
          <div className="d-flex flex-wrap gap-2 mt-2">
            {myScored.map((s) => {
              const complete = isRegNoLocallyComplete(s.registrationNumber);
              return (
                <Button
                  key={s.registrationNumber}
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => selectCandidate({
                    registrationNumber: s.registrationNumber,
                    name: s.name,
                    status: complete ? 'COMPLETE' : 'IN_PROGRESS',
                    scoredByExaminerId: user?.id,
                    scoredByExaminerName: user?.fullName,
                    underProtest: s.underProtest
                  })}
                >
                  {s.name} ({s.registrationNumber})
                  <Badge bg={complete ? 'success' : 'warning'} text={complete ? undefined : 'dark'} className="ms-1">
                    {complete ? 'Complete' : 'In Progress'}
                  </Badge>
                </Button>
              );
            })}
          </div>
        )
      )}
    </div>
  );

  const renderTeamReportSection = () => {
    if (!canSeeTeamReport) return null;
    return (
      <div className="mb-3 p-3" style={{ backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '6px' }}>
        <div className="d-flex justify-content-between align-items-center">
          <Button
            variant="link"
            className="p-0 fw-bold"
            onClick={() => setShowTeamReport((prev) => !prev)}
          >
            {showTeamReport ? 'Hide' : 'Show'} Team Report - Every Candidate
          </Button>
          {teamReport && (
            <span className="text-muted small">
              {teamReport.filter((r) => r.status === 'COMPLETE' || r.status === 'ABSENT').length} of {teamReport.length} accounted for
            </span>
          )}
        </div>
        {showTeamReport && (
          loadingReport ? (
            <div className="mt-2"><Spinner animation="border" size="sm" /></div>
          ) : !teamReport || teamReport.length === 0 ? (
            <div className="text-muted small mt-2">No candidates found for this module/exam centre roster.</div>
          ) : (
            <div className="table-responsive mt-2" style={{ maxHeight: 320 }}>
              <Table size="sm" bordered hover style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    <th>Registration Number</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Scored By</th>
                  </tr>
                </thead>
                <tbody>
                  {teamReport.map((r) => (
                    <tr
                      key={r.registrationNumber}
                      style={{ cursor: 'pointer' }}
                      onClick={() => selectCandidate({
                        registrationNumber: r.registrationNumber,
                        name: r.name,
                        status: r.status,
                        scoredByExaminerId: r.scoredByExaminerId,
                        scoredByExaminerName: r.scoredByExaminerName,
                        underProtest: r.underProtest
                      })}
                    >
                      <td>{r.registrationNumber}</td>
                      <td>{r.name}{r.underProtest && <Badge bg="danger" className="ms-1">Under Protest</Badge>}</td>
                      <td><Badge bg={statusBadgeVariant(r.status)} text={r.status === 'IN_PROGRESS' ? 'dark' : undefined}>{r.status.replace('_', ' ')}</Badge></td>
                      <td>{r.scoredByExaminerName || <span className="text-muted">-</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center my-3">
        <Spinner animation="border" size="sm" /> Loading scoring guide...
      </div>
    );
  }

  if (criteria.length === 0) {
    return (
      <Alert variant="warning">
        No scoring guide has been uploaded for this module yet. Please ask the admin to upload one before scores can be entered.
      </Alert>
    );
  }

  return (
    <div>
      {feedback && <Alert variant={feedback.type}>{feedback.message}</Alert>}

      {requiredQuestions < questionGroups.length && (
        <Alert variant="info" className="py-2">
          Candidates only need to fully score <strong>{requiredQuestions}</strong> of the {questionGroups.length}{' '}
          questions on this scoring guide to be marked complete.
        </Alert>
      )}

      {renderSearchSection()}
      {renderAddProtestSection()}
      {isTeamAssignment && renderMyScoredSection()}
      {renderTeamReportSection()}

      {!selectedCandidate ? (
        <Alert variant="info">Search or select a candidate above to begin scoring.</Alert>
      ) : (
        <div className="p-3 mb-3" style={{ backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '6px' }}>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
            <h6 className="mb-0 text-break">
              {selectedIsLocked || scoringRestrictedToOtherExaminer ? 'Reviewing' : 'Scoring'}: {selectedCandidate.name} ({selectedCandidate.registrationNumber})
              {selectedCandidate.underProtest && <Badge bg="danger" className="ms-2">Under Protest</Badge>}
            </h6>
            <Badge bg="info">{grandPercentage.toFixed(1)}%</Badge>
          </div>
          {selectedCandidate.status === 'ABSENT' && (
            <Alert variant="secondary" className="py-2">
              This candidate has been marked absent - scoring is not available.
            </Alert>
          )}
          {selectedCandidate.status === 'COMPLETE' && (
            <Alert variant="secondary" className="py-2">
              This candidate was already fully scored{selectedCandidate.scoredByExaminerName ? ` by ${selectedCandidate.scoredByExaminerName}` : ''} - scores are read-only.
            </Alert>
          )}
          {scoringRestrictedToOtherExaminer && (
            <Alert variant="secondary" className="py-2">
              This candidate is currently being scored by {selectedCandidate.scoredByExaminerName || 'another examiner'} - only they can continue scoring until this candidate is finalized.
            </Alert>
          )}
          {/* Once a candidate has any score at all (IN_PROGRESS, or beyond),
              they can no longer be marked absent - only a NOT_STARTED
              candidate (nothing entered yet on the scoring guide) can be. */}
          {selectedCandidate.status === 'NOT_STARTED' && (
            <div className="mb-2">
              <Button size="sm" variant="outline-danger" disabled={markingAbsent} onClick={() => handleMarkAbsent(selectedCandidate)}>
                {markingAbsent ? 'Marking...' : 'Mark Absent'}
              </Button>
            </div>
          )}
          <div className="table-responsive" style={{ maxHeight: '60vh' }}>
            <Table bordered size="sm" style={{ fontSize: '0.85rem' }}>
              <thead>
                <tr className="text-center align-middle">
                  <th style={{ minWidth: '50px' }}>Part</th>
                  <th style={{ minWidth: '180px', maxWidth: '260px' }}>Assessment Criteria</th>
                  <th style={{ minWidth: '220px', maxWidth: '320px' }}>Scoring Guide</th>
                  <th colSpan={2}>Max Score</th>
                  <th colSpan={2}>Score</th>
                </tr>
                <tr className="text-center">
                  <th colSpan={3}></th>
                  <th>P</th>
                  <th>R</th>
                  <th>P</th>
                  <th>R</th>
                </tr>
              </thead>
              <tbody>
                {questionGroups.map((group) => (
                  <React.Fragment key={group.questionNumber}>
                    <tr className="table-secondary">
                      <td colSpan={7} className="fw-bold">{group.questionLabel}</td>
                    </tr>
                    {group.items.map((c) => {
                      const cell = draftScores[c.id] || {};
                      return (
                        <tr key={c.id}>
                          <td className="text-center">{c.partLabel}</td>
                          <td style={{ maxWidth: '260px', whiteSpace: 'normal', wordBreak: 'break-word' }}>{c.assessmentCriteria}</td>
                          <td style={{ maxWidth: '320px', whiteSpace: 'normal', wordBreak: 'break-word' }}>{c.scoringGuide}</td>
                          <td className="text-center">{c.maxScoreProcess}</td>
                          <td className="text-center">{c.maxScoreProduct}</td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              style={{ width: '55px' }}
                              min={0}
                              max={c.maxScoreProcess}
                              value={cell.process ?? ''}
                              disabled={scoringLocked}
                              onChange={(e) => handleCellChange(c.id, 'process', e.target.value, c.maxScoreProcess)}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              style={{ width: '55px' }}
                              min={0}
                              max={c.maxScoreProduct}
                              value={cell.product ?? ''}
                              disabled={scoringLocked}
                              onChange={(e) => handleCellChange(c.id, 'product', e.target.value, c.maxScoreProduct)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="fw-bold">
                      <td colSpan={5} className="text-end">Sub-Total</td>
                      <td className="text-center">{groupTotal(group, 'process')}</td>
                      <td className="text-center">{groupTotal(group, 'product')}</td>
                    </tr>
                    <tr className="fw-bold table-light">
                      <td colSpan={5} className="text-end">TOTAL SCORE = PROCESS+PRODUCT</td>
                      <td colSpan={2} className="text-center">{groupTotal(group, 'process') + groupTotal(group, 'product')}</td>
                    </tr>
                  </React.Fragment>
                ))}
                <tr className="fw-bold table-dark">
                  <td colSpan={5} className="text-end">GRAND TOTAL (%)</td>
                  <td colSpan={2} className="text-center">{grandPercentage.toFixed(1)}%</td>
                </tr>
              </tbody>
            </Table>
          </div>
          {!selectedIsLocked && !scoringRestrictedToOtherExaminer && (
            <>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {autosaveStatus !== 'idle' && (
                  <span
                    className="small"
                    style={{
                      color: autosaveStatus === 'error' ? '#dc3545'
                        : autosaveStatus === 'saved' ? '#198754'
                        : autosaveStatus === 'queued' ? '#fd7e14'
                        : '#6c757d'
                    }}
                  >
                    {autosaveStatus === 'dirty' && 'Unsaved changes...'}
                    {autosaveStatus === 'saving' && 'Saving...'}
                    {autosaveStatus === 'saved' && 'All changes saved'}
                    {autosaveStatus === 'queued' && "Saved locally - you're offline, will sync automatically"}
                    {autosaveStatus === 'error' && 'Autosave failed - it will retry on your next edit'}
                  </span>
                )}
              </div>
              <span className="text-muted small d-block">
                Scores save automatically a couple of seconds after you stop typing - partial scores are fine, come back anytime.
                Scoring never locks on its own.
              </span>
              {isRegNoLocallyComplete(selectedCandidate.registrationNumber) && (
                <div className="mt-2">
                  <Button variant="success" size="sm" onClick={handleFinalize} disabled={finalizing}>
                    {finalizing ? 'Finalizing...' : 'Finalize & Lock Scores'}
                  </Button>
                  <div className="text-muted small mt-1">
                    You've scored the required number of questions for this candidate. Finalizing will permanently lock their scores.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Previewing the combined Final Marksheet/General Scoring Guide is a
          team-leader (or solo examiner) action. Read-only: no download of
          either document is offered here - PDFs are also generated and
          attached automatically for the required-document slots once the
          whole roster is complete (see the Auto-Generated Documents card
          above the upload section). */}
      {canSeeTeamReport && (
        <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
          <Button
            variant={teamAllReady ? 'success' : 'outline-secondary'}
            size="sm"
            onClick={() => setPreviewKind('marksheet')}
            disabled={!teamAllReady}
            title={teamAllReady ? '' : 'Available once every candidate on the roster is fully scored or marked absent'}
          >
            Preview Final Marksheet (PDF)
          </Button>
          <Button
            variant={teamAllReady ? 'success' : 'outline-secondary'}
            size="sm"
            onClick={() => setPreviewKind('guide')}
            disabled={!teamAllReady}
            title={teamAllReady ? '' : 'Available once every candidate on the roster is fully scored or marked absent'}
          >
            Preview General Scoring Guide (PDF)
          </Button>
        </div>
      )}
      <PdfPreviewModal
        show={!!previewKind}
        onClose={() => setPreviewKind(null)}
        title={previewKind === 'marksheet' ? 'Final Marksheet' : 'General Scoring Guide'}
        fetchUrl={
          previewKind === 'marksheet'
            ? `/api/scoring-criteria/assignment/${assignmentId}/marksheet/preview`
            : previewKind === 'guide'
            ? `/api/scoring-criteria/assignment/${assignmentId}/general-guide/preview`
            : null
        }
      />
    </div>
  );
}
